/**
 * Single source of truth for every cache name, channel name, and API URL that
 * the service worker (src/app/sw.ts), the debug log (lib/debug.ts), and the
 * data providers (lib/trip-data-context.tsx, lib/finance/finance-data-context.tsx)
 * must agree on. These used to be string literals scattered across all four —
 * renaming a cache in the SW silently broke Sync Now's clear list and the
 * debug tab's cache classification. Keep this module dependency-free so the
 * SW bundle can import it.
 */

export const CACHE_NAMES = {
  /** Trip API responses + the app-wide sync timestamp (app-managed). */
  tripData: "trip-api-data",
  /** Hero images — lazily cached by the SW on first view (CacheFirst on
   * /images/*); the Debug panel's offline download fills it too. */
  tripImages: "trip-images",
  /** Finance API responses + last-refreshed timestamp (app-managed). */
  financeData: "finance-data-v1",
  /** Runtime SWR cache of page navigations (SW-managed). */
  pages: "pages",
  /** NextAuth session responses so auth resolves offline (SW-managed). */
  authSession: "auth-session",
  /** Next.js static chunks (SW-managed). */
  nextStatic: "next-static",
  /** App-shell statics that only change on deploy — the PWA manifest, the app
   * icon, the favicon. CacheFirst so they never hit the network on navigation
   * (they otherwise fell through to serwist's default NetworkFirst JSON cache,
   * which re-fetched manifest.json on every click). */
  appShell: "app-shell",
  /** Persisted debug event log. */
  debugLog: "debug-log",
} as const;

/** BroadcastChannel the SW uses to stream fetch telemetry to open pages. */
export const DEBUG_CHANNEL = "trip-debug";

/** Ring-buffer size shared by the SW's in-worker log and the page-side log. */
export const DEBUG_MAX_EVENTS = 300;

/** One SW-handled fetch, as broadcast on DEBUG_CHANNEL. */
export interface SwFetchEvent {
  session: string;
  seq: number;
  ts: number;
  url: string;
  cacheName: string;
  strategy: string;
  source: string;
  durationMs?: number;
  status?: number;
  /** Response size: wire bytes when Content-Length was present, else decoded body size. */
  bytes?: number;
}

/** App-wide "last synced" timestamp key in the trip data cache. Written by
 * cold loads, Sync Now reloads, and the Debug panel's offline download —
 * they all establish one sync generation for every trip. */
export const SYNC_TS_KEY = "/_sync-timestamp";

/* ── Trip API URLs (also the Cache Storage keys for those responses) ────── */

export const TRIPS_URL = "/api/trip?type=trips";
export const tripDataUrl = (tripId: string) => `/api/trip?type=all&trip=${tripId}`;
export const tripVersionUrl = (tripId: string) => `/api/trip?type=version&trip=${tripId}`;
export const TRIP_MANIFEST_URL = "/api/trip?type=manifest";
/** Cache key of the client's applied trip manifest — hashes describe what's
 * IN this device's cache, updated per-bundle as syncs land (never blanket-
 * copied from the server, or one trip's sync would mask another's staleness). */
export const TRIP_MANIFEST_KEY = "/_trip/manifest";
/** Per-trip last-synced timestamp key (incremental sync is per-trip, so the
 * old app-wide generation no longer applies; SYNC_TS_KEY remains as a read
 * fallback for data written by older builds). */
export const tripSyncTsKey = (tripId: string) => `/_sync-timestamp?trip=${tripId}`;

/* ── Finance API URLs ───────────────────────────────────────────────────── */

export const FINANCE_CORE_URL = "/api/finance?type=core";
export const FINANCE_TXN_URL = "/api/finance?type=transactions";
export const FINANCE_VERSION_URL = "/api/finance?type=version";
export const FINANCE_MANIFEST_URL = "/api/finance?type=manifest";
export const financeShardUrl = (id: string) => `/api/finance?type=shard&id=${encodeURIComponent(id)}`;
/** Batched shard fetch — one request for the whole changed set. The response
 * is split client-side and cached per shard under financeShardUrl keys, so
 * offline reads and cache healing are unchanged. */
export const financeShardsBatchUrl = (ids: string[]) =>
  `/api/finance?type=shards&ids=${ids.map(encodeURIComponent).join(",")}`;
/** Cache key of the client's applied finance manifest (see TRIP_MANIFEST_KEY). */
export const FINANCE_MANIFEST_KEY = "/_finance/manifest";
